import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';

const ActivityHistoryTab = ({ vendor, approvalHistory = [] }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');

  // Convert approval history to activities
  const activities = approvalHistory.map(approval => ({
    id: approval.id,
    type: 'approvals',
    title: `${approval.level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Approval`,
    description: approval.comments || 'No comments provided',
    timestamp: new Date(approval.created_at).toLocaleTimeString(),
    date: new Date(approval.created_at).toLocaleDateString(),
    user: approval.approver?.full_name || approval.approver?.name || approval.decided_by_email || 'System',
    severity: approval.status === 'approved' ? 'success' : approval.status === 'rejected' ? 'error' : 'info',
    icon: approval.status === 'approved' ? 'CheckCircle' : approval.status === 'rejected' ? 'XCircle' : 'Clock',
    details: {
      'Status': approval.status.replace(/\b\w/g, l => l.toUpperCase()),
      'Level': approval.level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      'Approved By': approval.approver?.full_name || approval.approver?.name || approval.decided_by_email || 'System',
      'Date': new Date(approval.created_at).toLocaleDateString()
    }
  }));

  // Update activity filters with actual counts
  const activityFilters = [
    { id: 'all', label: 'All Activities', count: activities.length },
    { id: 'profile', label: 'Profile Changes', count: 0 },
    { id: 'documents', label: 'Documents', count: 0 },
    { id: 'approvals', label: 'Approvals', count: activities.filter(a => a.type === 'approvals').length },
    { id: 'orders', label: 'Orders', count: 0 },
    { id: 'payments', label: 'Payments', count: 0 }
  ];

  const dateRanges = [
    { id: 'all', label: 'All Time' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'quarter', label: 'This Quarter' }
  ];



  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success':
        return 'text-emerald-700 bg-emerald-50';
      case 'warning':
        return 'text-amber-700 bg-amber-50';
      case 'error':
        return 'text-red-700 bg-red-50';
      case 'info':
      default:
        return 'text-blue-700 bg-blue-50';
    }
  };

  const filteredActivities = selectedFilter === 'all' 
    ? activities 
    : activities.filter(activity => activity.type === selectedFilter);

  return (
    <div className="space-y-5">
      {/* Activity Filters */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Activity Filters</h3>
              <div className="flex flex-wrap gap-2">
                {activityFilters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedFilter(filter.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedFilter === filter.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    <span>{filter.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      selectedFilter === filter.id
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <select
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
              >
              {dateRanges.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Icon name="Activity" size={20} className="text-slate-600" />
            Activity Timeline
          </h3>
        
        {filteredActivities.length > 0 ? (
          <div className="space-y-6">
            {filteredActivities.map((activity, index) => (
              <div key={activity.id} className="relative">
                {/* Timeline Line */}
                {index < filteredActivities.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-16 bg-slate-200"></div>
                )}
                
                <div className="flex items-start space-x-4">
                  {/* Activity Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getSeverityColor(activity.severity)}`}>
                    <Icon name={activity.icon} size={20} />
                  </div>
                  
                  {/* Activity Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{activity.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">{activity.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-slate-600">{activity.timestamp}</div>
                        <div className="text-xs text-slate-600">{activity.date}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-slate-600 mb-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="User" size={14} />
                        <span>By {activity.user}</span>
                      </div>
                    </div>
                    
                    {/* Activity Details */}
                    {activity.details && (
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <h5 className="font-medium text-slate-900 mb-2">Details:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(activity.details).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-sm text-slate-600 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').trim()}:
                              </span>
                              <span className="text-sm text-slate-900 font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Activity" size={64} className="text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Activity History</h3>
            <p className="text-slate-600 mb-6">This vendor doesn't have any activity history yet.</p>
            <p className="text-sm text-slate-600">Activity will appear here once the vendor starts interacting with the system.</p>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Activity Summary */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={20} className="text-slate-600" />
            Activity Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">{activities.length}</div>
              <div className="text-sm text-slate-600">Total Activities</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">
                {activities.filter(a => {
                  const activityDate = new Date(a.date);
                  const now = new Date();
                  return activityDate.getMonth() === now.getMonth() && activityDate.getFullYear() === now.getFullYear();
                }).length}
              </div>
              <div className="text-sm text-slate-600">This Month</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">
                {activities.filter(a => {
                  const activityDate = new Date(a.date);
                  const now = new Date();
                  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                  return activityDate >= weekAgo;
                }).length}
              </div>
              <div className="text-sm text-slate-600">This Week</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">
                {activities.filter(a => {
                  const activityDate = new Date(a.date);
                  const now = new Date();
                  return activityDate.toDateString() === now.toDateString();
                }).length}
              </div>
              <div className="text-sm text-slate-600">Today</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityHistoryTab;